import {
  API_KEYS_ARTICLE,
  API_KEYS_DESCRIPTION,
  BINANCE_API_KEYS_PATH,
  CONNECT_DEMO_ACCOUNT_EXTRA_INFO_ARTICLE_ID,
  CONNECT_DEMO_ACCOUNT_EXTRA_INFO_CONTENT,
  CONNECT_DEMO_ACCOUNT_EXTRA_INFO_TITLE,
} from "@shared/features/onboarding/constants";
import {
  getCreateApiLink,
  getQuickConnectGuideList,
  getSignUpLink,
} from "@shared/features/onboarding/utils";
import React from "react";
import { PRIMARY_EXCHANGES } from "@shared/common/constants";
import Link from "common/Typography/Link";
import IntercomLink from "common/Text/IntercomLink";
import Text from "common/Typography/Text";
import { ExtraInfoType } from "@shared/features/onboarding/types";
import styled from "styled-components";
import useSharedConnectExchangeExtraInfo from "@shared/features/portfolio/createAccount/useSharedConnectExchangeExtraInfo";
import { ConfigurationExchangeInfo } from "@shared/types";

interface UseConnectExchangeExtraInfoProps {
  primaryExchange?: string;
  selectedSubExchange?: ConfigurationExchangeInfo;
  isQuickConnect?: boolean;
}

const useConnectExchangeExtraInfo = ({
  primaryExchange,
  selectedSubExchange,
  isQuickConnect,
}: UseConnectExchangeExtraInfoProps) => {
  const { createApiKeysGuideList, isDemoWorkspace, exchangesData } =
    useSharedConnectExchangeExtraInfo({
      primaryExchange,
      selectedSubExchange,
    });

  let quickConnectGuideList: (string | JSX.Element)[] =
    getQuickConnectGuideList(primaryExchange);
  if (
    isQuickConnect &&
    primaryExchange &&
    primaryExchange === PRIMARY_EXCHANGES.BINANCE
  ) {
    quickConnectGuideList = [
      <>
        Make sure you’ve accepted{" "}
        <Link href={BINANCE_API_KEYS_PATH} target={"_blank"}>
          Binance’s API Key Terms of Use
        </Link>
      </>,
      ...quickConnectGuideList,
    ];
  }

  const guideItemsList = isQuickConnect
    ? quickConnectGuideList
    : createApiKeysGuideList;

  const createApiLink = getCreateApiLink(primaryExchange, exchangesData);

  const connectApiGuide = (
    <div style={{ width: "100%" }}>
      {guideItemsList?.map((item, index) => (
        <ListItem key={index}>
          {index + 1}. {item}
        </ListItem>
      ))}
      {primaryExchange === PRIMARY_EXCHANGES.HYPERLIQUID && (
        <Text style={{ marginTop: 8, fontStyle: "italic" }}>
          Note: You only need your 'Account Wallet Address', and not your 'API
          Wallet Address'. Make sure to carefully follow the steps in Section 6.
        </Text>
      )}
      {!isQuickConnect && createApiLink && (
        <IntercomLink
          articleLink={createApiLink}
          style={{ display: "inline-block", marginTop: 4 }}
        />
      )}
    </div>
  );

  const signUpExchangeLink = (
    <Link href={getSignUpLink(primaryExchange, exchangesData)} target="_blank">
      Sign up for free at {primaryExchange}
    </Link>
  );

  const apiKeysDescription = (
    <>
      <Text style={{ marginBottom: 8 }}>{API_KEYS_DESCRIPTION}</Text>
      <IntercomLink articleId={API_KEYS_ARTICLE} />
    </>
  );

  const extraInfoToTitle = {
    [ExtraInfoType.CONNECT_API]: !isQuickConnect
      ? `Manually connect an API on ${primaryExchange}`
      : `Quick connect an API with ${primaryExchange}`,
    [ExtraInfoType.NO_EXCHANGE_ACCOUNT]: `Don’t have a ${primaryExchange} account?`,
    [ExtraInfoType.API_KEYS]: "Why do we need your API keys?",
  };

  const extraInfoToDescription = {
    [ExtraInfoType.CONNECT_API]: connectApiGuide,
    [ExtraInfoType.NO_EXCHANGE_ACCOUNT]: signUpExchangeLink,
    [ExtraInfoType.API_KEYS]: apiKeysDescription,
  };

  const demoAccountExtraInfo = {
    title: CONNECT_DEMO_ACCOUNT_EXTRA_INFO_TITLE,
    content: (
      <>
        <Text style={{ marginBottom: 8 }}>
          {CONNECT_DEMO_ACCOUNT_EXTRA_INFO_CONTENT}
        </Text>
        <IntercomLink articleId={CONNECT_DEMO_ACCOUNT_EXTRA_INFO_ARTICLE_ID} />
      </>
    ),
  };

  return {
    isDemoWorkspace,
    extraInfoToTitle,
    extraInfoToDescription,
    demoAccountExtraInfo,
  };
};

export default useConnectExchangeExtraInfo;

const ListItem = styled(Text)`
  &:not(:last-child) {
    margin-bottom: 4px;
  }
`;
